import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import withRouter from 'utils/with-router';
import getLogoTarget from 'components/header/header-logo';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import qs from 'query-string';
import loadImage from 'utils/load-image';
import types from 'utils/prop-types';
import constants from 'utils/constants';
import { isImpelMember, isBlokurUser, isDndSubscriber } from 'utils/auth';
import { pushToast } from 'ducks/modules/toaster';
import { logout } from 'ducks/modules/user';
import { openUploadModal, closeUploadModal } from 'ducks/modules/upload';
import { openCalendlyModal, closeCalendlyModal } from 'ducks/modules/calendly';
import Icon from 'components/icon/icon';
import SearchPreview from 'components/search-preview/search-preview';
import DisplayPicture from 'components/display-picture/display-picture';
import UploadModal from 'components/upload-modal/upload-modal';
import 'components/header/header.scss';
import NotificationsMenu from 'components/notifications-menu/notifications-menu';
import ActionModal from 'components/action-modal/action-modal';
import Calendly from 'components/calendly/calendly';
import { isDesktop, isTablet, isMobileOnly } from 'react-device-detect';
import { getWorkUrl } from '../../utils/works';

const Header = (props) => {
    const [search, setSearch] = useState('');
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [notificationsMenuOpen, setNotificationsMenuOpen] = useState(false);
    const [mobileViewOpen, setMobileViewOpen] = useState(false);
    const [autoCompleteIndex, setAutoCompleteIndex] = useState(-1);
    const [autoCompleteResults, setAutoCompleteResults] = useState([]);

    const notificationsMenuToggler = useRef(null);
    const notificationsMenuRef = useRef(null);
    const userMenuToggler = useRef(null);
    const userMenuRef = useRef(null);

    const location = useLocation();

    const { user, minimal, hideSearchBar, notifications, publicView } = props;

    const canGetNotifications =
        user && user.hasOwnProperty('permissions') && user.permissions.includes('notifications:GET');

    /**
     * Listen for click events and close the relevant menus
     * @param {Object} e
     */
    const handleClickOutsideMenus = (e) => {
        if (
            _.get(userMenuRef, 'current') &&
            _.get(userMenuToggler, 'current') &&
            !userMenuRef.current.contains(e.target) &&
            !userMenuToggler.current.contains(e.target)
        ) {
            setUserMenuOpen(false);
        }
        if (
            _.get(notificationsMenuRef, 'current') &&
            _.get(notificationsMenuToggler, 'current') &&
            !notificationsMenuRef.current.contains(e.target) &&
            !notificationsMenuToggler.current.contains(e.target)
        ) {
            setNotificationsMenuOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsideMenus);
    }, []);

    return (
        <>
            <header className="bk-site-header">
                <nav
                    className={'navbar' + (isMobileOnly ? ' mobile' : '')}
                    role="navigation"
                    aria-label="main navigation">
                    <div className={'navbar-brand logo-navbar' + (isMobileOnly ? ' mobile' : '')}>
                        <div className="logo">
                            <Link
                                to={getLogoTarget({ pathname: location.pathname, publicView })}
                                className="navbar-item"
                                data-cy="dnd-header-logo">
                                <img alt="Blòkur" src={loadImage('blokur-logo-whiteandblue.svg')} />
                            </Link>
                        </div>

                        <a
                            role="button"
                            onClick={() => {
                                setMobileViewOpen(!mobileViewOpen);
                            }}
                            className={`navbar-burger${mobileViewOpen ? ' is-active' : ''}`}>
                            <span aria-hidden="true"></span>
                            <span aria-hidden="true"></span>
                            <span aria-hidden="true"></span>
                        </a>
                    </div>
                    <div className={`navbar-menu${mobileViewOpen ? ' is-active' : ''}`}>
                        <div className="navbar-start search-navbar">
                            {!minimal && !hideSearchBar && (isDesktop || isTablet) && (
                                <form
                                    className="search"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (autoCompleteIndex >= 0 && autoCompleteIndex < autoCompleteResults.length) {
                                            props.history.push({
                                                pathname: getWorkUrl(
                                                    autoCompleteResults[autoCompleteIndex]._source.bk_work_id,
                                                ),
                                            });
                                        } else {
                                            props.history.push({
                                                pathname: '/works',
                                                search: qs.stringify(
                                                    {
                                                        start: 0,
                                                        search,
                                                        filter: 'any',
                                                        fields: [],
                                                        sort: 'popularity',
                                                    },
                                                    {
                                                        arrayFormat: 'none',
                                                    },
                                                ),
                                            });
                                        }
                                        setSearch('');
                                    }}>
                                    <div className="bk-input icon-left">
                                        <Icon icon="search" />
                                        <input
                                            placeholder="Search"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onClick={(e) => setSearch(e.target.value)}
                                        />
                                        <SearchPreview
                                            className="dark"
                                            input={search}
                                            onChange={(results) => setAutoCompleteResults(results)}
                                            onIndexChanged={(index) => setAutoCompleteIndex(index)}
                                        />
                                    </div>
                                </form>
                            )}
                        </div>
                        <ul className="navbar-end">
                            {!publicView &&
                                !minimal &&
                                user &&
                                user.hasOwnProperty('source_type') &&
                                user.features &&
                                user.features.licensing_messenger && (
                                    <li className="navbar-item">
                                        <Link
                                            to={{
                                                pathname: '/inbox',
                                            }}>
                                            <Icon icon="inbox" />
                                            Inbox
                                        </Link>
                                    </li>
                                )}
                            {!publicView && !minimal && user.features && user.features.streaming_licensing && (
                                <li className="navbar-item">
                                    <Link
                                        to={{
                                            pathname: '/licensing',
                                        }}>
                                        <Icon icon="dollar-sign" />
                                        Licensing
                                    </Link>
                                </li>
                            )}
                            {!publicView &&
                                !minimal &&
                                user.features &&
                                user.features.rights_management &&
                                !(isDndSubscriber() && !isBlokurUser()) && (
                                    <li className="navbar-item">
                                        <Link
                                            to={{
                                                pathname: '/works',
                                                search: `filter=any&search=&sort=popularity&start=0`,
                                            }}>
                                            Browse
                                        </Link>
                                    </li>
                                )}
                            {!publicView &&
                                !minimal &&
                                user &&
                                user.hasOwnProperty('source_type') &&
                                user.source_type !== constants.UserSourceTypes.MUSIC &&
                                !user?.user_preferences?.impel?.admin && (
                                    <li className="navbar-item">
                                        <a onClick={() => props.openUploadModal()}>
                                            <Icon icon="upload-cloud" />
                                            Upload
                                        </a>
                                    </li>
                                )}
                            {!publicView && !minimal && user?.user_preferences?.impel && (
                                <li className="navbar-item">
                                    <Link
                                        to={{
                                            pathname: '/mandates',
                                            search: `${
                                                user?.user_preferences?.impel?.admin ? 'page=1&pagesize=15' : ''
                                            }`,
                                        }}>
                                        <Icon icon="file-plus" />
                                        Mandates
                                    </Link>
                                </li>
                            )}
                            {!publicView &&
                                !minimal &&
                                user?.features?.rights_management &&
                                user?.source_type !== constants.UserSourceTypes.MUSIC &&
                                !isImpelMember() && (
                                    <li className="navbar-item">
                                        <a
                                            onClick={() =>
                                                props.openCalendlyModal(
                                                    'Catalogue Export',
                                                    "Schedule a call with Blòkur's Customer Success Team to unlock this feature.",
                                                    'Upgrade',
                                                )
                                            }>
                                            <Icon icon="download" />
                                            Export
                                        </a>
                                    </li>
                                )}
                            {!publicView && isImpelMember() && (
                                <li className="navbar-item">
                                    <Link
                                        to={{
                                            pathname: '/file-status',
                                        }}>
                                        <Icon icon="file-text" />
                                        Reports
                                    </Link>
                                </li>
                            )}
                            {!publicView && (
                                <li
                                    className="navbar-item"
                                    onClick={() => {
                                        window &&
                                            window.HubSpotConversations &&
                                            window.HubSpotConversations.widget.open();
                                    }}>
                                    <Icon icon="help-circle" />
                                    Support
                                </li>
                            )}
                            {!publicView && !minimal && canGetNotifications && (isDesktop || isTablet) && (
                                <li
                                    className="navbar-item menu-hook"
                                    ref={(ref) => {
                                        notificationsMenuToggler.current = ref;
                                    }}>
                                    <Icon
                                        icon="bell"
                                        badge={(notifications.items.filter((n) => !n.read).length > 0).toString()}
                                        onClick={() => {
                                            setNotificationsMenuOpen(!notificationsMenuOpen);
                                        }}
                                    />
                                    <div ref={(ref) => (notificationsMenuRef.current = ref)}>
                                        <NotificationsMenu
                                            open={notificationsMenuOpen}
                                            closeMenu={() => setNotificationsMenuOpen(false)}
                                        />
                                    </div>
                                </li>
                            )}
                            <li
                                className={
                                    'navbar-item account-menu' +
                                    (userMenuOpen ? ' is-active' : '') +
                                    (isMobileOnly ? ' mobile' : '')
                                }
                                ref={userMenuToggler}
                                onClick={() => {
                                    setUserMenuOpen(!userMenuOpen);
                                }}>
                                <DisplayPicture user={user} />
                                {isMobileOnly ? 'Account' : ''}
                                <Icon className={'menu-arrow ' + (userMenuOpen ? 'open' : '')} icon="chevron-down" />
                                <ul
                                    onClick={() => setUserMenuOpen(false)}
                                    className={'menu navbar-dropdown' + (userMenuOpen ? ' open' : '')}
                                    ref={userMenuRef}>
                                    {!minimal && (
                                        <li className="navbar-item menu__item--hoverable">
                                            <Link to="/">Dashboard</Link>
                                        </li>
                                    )}
                                    {!minimal && (
                                        <li className="navbar-item menu__item--hoverable">
                                            <Link to="/profile/settings">Account settings</Link>
                                        </li>
                                    )}
                                    {(() => {
                                        return !publicView ? (
                                            <li className="navbar-item menu__item--hoverable" onClick={props.logout}>
                                                <a>Logout</a>
                                            </li>
                                        ) : (
                                            <li className="navbar-item menu__item--hoverable">
                                                <a href="/sign-up/draganddrop">Signup</a>
                                            </li>
                                        );
                                    })()}
                                </ul>
                            </li>
                        </ul>
                    </div>
                </nav>
                <UploadModal isOpen={props.upload.modalOpen} closeModal={() => props.closeUploadModal()} />
                <ActionModal
                    isOpen={props.calendly.modalOpen}
                    title={props.calendly.modalTitle}
                    icon={props.calendly.modalTitle === 'Blòkur Insights' ? 'bar-chart' : 'download'}
                    isForUpgrade={true}
                    content={
                        <div>
                            <p>{props.calendly.modalContent}</p>

                            <Calendly />
                        </div>
                    }
                    actions={[{ name: 'Close', buttonClass: 'cancel' }]}
                    closeModal={() => {
                        props.closeCalendlyModal();
                    }}
                />
            </header>
        </>
    );
};

const mapStateToProps = (state) => ({
    user: state.user,
    upload: state.upload,
    calendly: state.calendly,
    notifications: state.notifications,
});

const mapDispatchToProps = (dispatch) => ({
    logout: () => dispatch(logout()),
    pushToast: (toast) => dispatch(pushToast(toast)),
    closeUploadModal: () => dispatch(closeUploadModal()),
    openUploadModal: () => dispatch(openUploadModal()),
    closeCalendlyModal: () => dispatch(closeCalendlyModal()),
    openCalendlyModal: (title, content, action) => dispatch(openCalendlyModal(title, content, action)),
});

Header.propTypes = {
    user: types.user.isRequired,
    logout: PropTypes.func,
    history: types.history,
    minimal: PropTypes.bool,
    publicView: PropTypes.bool,
    pushToast: PropTypes.func,
    upload: types.upload,
    closeUploadModal: PropTypes.func,
    openUploadModal: PropTypes.func,
    closeCalendlyModal: PropTypes.func,
    openCalendlyModal: PropTypes.func,
    hideSearchBar: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Header));
